// src/hooks/usePushNotifications.js
//
// State + actions for the "Notifications" opt-in on the patient settings page.
//
// status:
//   loading      — still working out what is possible here
//   unavailable  — push can't work (server has no keys, or no service worker, e.g. `npm start`)
//   needs-install— iPhone/iPad Safari: push only works once added to the Home Screen
//   unsupported  — this browser has no Web Push
//   denied       — the person blocked notifications in the browser
//   off / on     — usable; subscribed on this device or not

import { useCallback, useEffect, useState } from 'react';
import { pushAPI } from '../services/pushAPI';
import {
  getRegistration, isIos, isStandalone, pushSupported, urlBase64ToUint8Array,
} from '../utils/pushSubscription';

export default function usePushNotifications() {
  const [status, setStatus] = useState('loading');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [testSent, setTestSent] = useState(false);

  const refresh = useCallback(async () => {
    if (!pushSupported()) {
      setStatus(isIos() && !isStandalone() ? 'needs-install' : 'unsupported');
      return;
    }
    try {
      const reg = await getRegistration();
      if (!reg) { setStatus('unavailable'); return; }
      const { data } = await pushAPI.config();
      if (!data.enabled) { setStatus('unavailable'); return; }
      if (Notification.permission === 'denied') { setStatus('denied'); return; }
      const sub = await reg.pushManager.getSubscription();
      setStatus(sub ? 'on' : 'off');
    } catch (e) {
      setStatus('unavailable');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Must run from a click handler — browsers only show the permission prompt for a user gesture.
  const enable = useCallback(async () => {
    setBusy(true);
    setError('');
    setTestSent(false);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setStatus(permission === 'denied' ? 'denied' : 'off');
        return;
      }
      const reg = await getRegistration();
      const { data } = await pushAPI.config();
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey),
      });
      try {
        await pushAPI.subscribe(sub.toJSON());
      } catch (e) {
        await sub.unsubscribe(); // don't leave a device subscribed that the server doesn't know about
        throw e;
      }
      setStatus('on');
    } catch (e) {
      setError(e.response?.data?.message || 'Could not turn on notifications. Please try again.');
    } finally {
      setBusy(false);
    }
  }, []);

  const disable = useCallback(async () => {
    setBusy(true);
    setError('');
    setTestSent(false);
    try {
      const reg = await getRegistration();
      const sub = reg && await reg.pushManager.getSubscription();
      if (sub) {
        await pushAPI.unsubscribe(sub.endpoint);
        await sub.unsubscribe();
      }
      setStatus('off');
    } catch (e) {
      setError('Could not turn off notifications. Please try again.');
    } finally {
      setBusy(false);
    }
  }, []);

  const sendTest = useCallback(async () => {
    setBusy(true);
    setError('');
    setTestSent(false);
    try {
      const { data } = await pushAPI.test();
      if (data.subscribed) setTestSent(true);
      else setError('The test notification could not be delivered. Try turning notifications off and on again.');
    } catch (e) {
      setError('Could not send a test notification.');
    } finally {
      setBusy(false);
    }
  }, []);

  return { status, busy, error, testSent, enable, disable, sendTest };
}
